/**
 * Cada generación con IA, de una en una.
 *
 * La otra pestaña («Lo que falta») cuenta, agrupado, lo que no se pudo hacer:
 * sirve para decidir qué construir. Ésta enseña el caso entero, para revisarlo
 * cuando algo salió raro: el prompt del cliente, lo que la IA planeó —antes de
 * que el código recortara nada—, lo que se construyó con qué bloque, lo que no
 * cupo y el proyecto que salió. Si planeó más secciones de las que se
 * construyeron, se dice arriba: es lo primero que hay que mirar.
 *
 * Una generación sin proyecto es un borrador que nadie terminó.
 */
import { useCallback, useEffect, useState } from 'react'
import { IconExternalLink } from '@tabler/icons-react'
import { VelticaMascot } from '@webbuilder/ui'
import { PUBLIC_SITE_DOMAIN, listAiGenerations, type AdminAiGeneration } from '../../lib/api'
import { editorHref } from '../../lib/editorSession'
import { activeLanguage, translateServerError, useI18n } from '../../lib/i18n'

export function AdminAiGenerations({ token, days }: { token: string; days: number }) {
  const { t } = useI18n()
  const [rows, setRows] = useState<AdminAiGeneration[] | null>(null)
  const [more, setMore] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [failed, setFailed] = useState('')

  const load = useCallback(async () => {
    setFailed('')
    setRows(null)
    try {
      const list = await listAiGenerations(token, days)
      setRows(list)
      setMore(list.length === 40)
    } catch (err) {
      setFailed(translateServerError(err, t('aiUnmet.loadFailed')))
      setRows([])
    }
  }, [token, days, t])

  useEffect(() => { void load() }, [load])

  async function loadMore() {
    const last = rows?.[rows.length - 1]
    if (!last) return
    setLoadingMore(true)
    try {
      const list = await listAiGenerations(token, days, last.createdAt)
      setRows((current) => [...(current ?? []), ...list])
      setMore(list.length === 40)
    } catch (err) {
      setFailed(translateServerError(err, t('aiUnmet.loadFailed')))
    } finally {
      setLoadingMore(false)
    }
  }

  const when = (iso: string) => new Date(iso).toLocaleString(activeLanguage(), { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })

  if (rows === null) return <p className="dash-card-description">{t('aiUnmet.loading')}</p>

  return (
    <>
      {failed ? (
        <div className="dash-inline-msg">
          <span>{failed}</span>
          <div className="dash-inline-msg-actions">
            <button type="button" className="dash-secondary-btn" onClick={() => { void load() }}>{t('aiUnmet.retry')}</button>
          </div>
        </div>
      ) : null}

      {rows.length === 0 ? (
        <div className="dash-placeholder">
          <VelticaMascot outline size={36} />
          <strong>{t('aiGen.empty')}</strong>
          <span>{t('aiGen.emptyBody')}</span>
        </div>
      ) : (
        <div className="dash-ai-gen-list">
          {rows.map((row) => <GenerationCard key={row.id} row={row} when={when(row.createdAt)} />)}
        </div>
      )}

      {more ? (
        <div className="dash-ai-gen-more">
          <button type="button" className="dash-secondary-btn" disabled={loadingMore} onClick={() => { void loadMore() }}>{t('aiGen.loadMore')}</button>
        </div>
      ) : null}
    </>
  )
}

function GenerationCard({ row, when }: { row: AdminAiGeneration; when: string }) {
  const { t } = useI18n()
  const planned = (row.plan.planned ?? []).reduce((sum, page) => sum + page.length, 0)
  const built = (row.plan.built ?? []).reduce((sum, page) => sum + page.sections.length, 0)
  /* Lo primero que hay que mirar: secciones que la IA planeó y no salieron. */
  const lost = row.status === 'ok' && planned > built ? planned - built : 0
  const title = row.project?.name ?? row.businessName ?? t('aiGen.untitled')
  const siteUrl = row.project?.subdomain ? `https://${row.project.subdomain}.${PUBLIC_SITE_DOMAIN}` : null
  const totalCost = row.plan.cost ? formatUsd(row.plan.cost.totalUsd) : row.costRange
    ? `${formatUsd(row.costRange.minUsd)} – ${formatUsd(row.costRange.maxUsd)}` : null

  return (
    <details className={`dash-ai-gen${row.status === 'failed' ? ' is-failed' : ''}`}>
      <summary>
        <span className="dash-ai-gen-head">
          <strong>{title}</strong>
          <small>{[when, t(`aiGen.flow.${row.flow}`), row.userEmail].filter(Boolean).join(' · ')}</small>
        </span>
        <span className="dash-ai-gen-tags">
          {totalCost ? <em className="dash-billing-pill is-soft">{t('aiGen.cost.summary', { amount: totalCost })}</em> : null}
          {row.status === 'failed' ? <em className="dash-billing-pill is-danger">{t('aiGen.failed')}</em> : null}
          {lost ? <em className="dash-billing-pill is-warn">{t('aiGen.lost', { count: lost })}</em> : null}
          {row.unmet.length ? <em className="dash-billing-pill is-soft">{t('aiGen.unmetCount', { count: row.unmet.length })}</em> : null}
          {row.status === 'ok' && !row.project ? <em className="dash-billing-pill is-soft">{t(row.flow === 'project' ? 'aiGen.notCreated' : 'aiGen.noProject')}</em> : null}
        </span>
      </summary>

      <div className="dash-ai-gen-body">
        <section>
          <h4>{t('aiGen.prompt')}</h4>
          <p className="dash-ai-gen-prompt">{row.prompt}</p>
        </section>

        {row.error ? (
          <section>
            <h4>{t('aiGen.error')}</h4>
            <p className="dash-ai-gen-error">{row.error}</p>
          </section>
        ) : null}

        {row.plan.built?.length ? (
          <section>
            <h4>{t('aiGen.sections', { planned, built })}</h4>
            {(row.plan.built ?? []).map((page, pageIndex) => (
              <div key={pageIndex} className="dash-ai-gen-page">
                {(row.plan.built ?? []).length > 1 ? <strong>{page.name}</strong> : null}
                {/* Lo que mandó la IA tal cual, para comparar con lo construido. */}
                {row.plan.planned?.[pageIndex]?.length ? (
                  <small>{t('aiGen.planned', { list: row.plan.planned[pageIndex].join(', ') })}</small>
                ) : null}
                <ol>
                  {page.sections.map((section, index) => (
                    <li key={index}>
                      <span>{section.headline || section.category}</span>
                      <small>{[section.category, section.template, section.items ? t('aiGen.items', { count: section.items }) : ''].filter(Boolean).join(' · ')}</small>
                    </li>
                  ))}
                </ol>
              </div>
            ))}
            {row.plan.extraSections || row.plan.itemsRequested ? (
              <small className="dash-ai-gen-note">
                {[
                  row.plan.extraSections ? t('aiGen.extraSections', { count: row.plan.extraSections }) : '',
                  row.plan.itemsRequested ? t('aiGen.itemsRequested', { count: row.plan.itemsRequested }) : '',
                ].filter(Boolean).join(' · ')}
              </small>
            ) : null}
          </section>
        ) : null}

        {row.unmet.length ? (
          <section>
            <h4>{t('aiGen.unmet')}</h4>
            <ul className="dash-ai-gen-unmet">
              {row.unmet.map((item, index) => (
                <li key={index}>
                  <span>{item.what}</span>
                  <small>{[t(`aiUnmet.kind.${item.kind}`), t(item.source === 'code' ? 'aiUnmet.fromCode' : 'aiUnmet.fromAi')].join(' · ')}</small>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        <footer className="dash-ai-gen-foot">
          <div className="dash-ai-gen-metrics">
            <small>{[row.model, row.language, row.tokens !== null ? t('aiGen.tokens', { count: row.tokens }) : '', row.durationMs ? `${(row.durationMs / 1000).toFixed(1)} s` : ''].filter(Boolean).join(' · ')}</small>
            <GenerationCost row={row} />
          </div>
          <span className="dash-ai-gen-links">
            {row.project ? (
              <a className="dash-secondary-btn" href={editorHref(`?pid=${row.project.id}`)} target="_blank" rel="noreferrer">
                {t('aiGen.openEditor')} <IconExternalLink size={13} aria-hidden="true" />
              </a>
            ) : null}
            {siteUrl ? (
              <a className="dash-secondary-btn" href={siteUrl} target="_blank" rel="noreferrer">
                {t('aiGen.openSite')} <IconExternalLink size={13} aria-hidden="true" />
              </a>
            ) : null}
          </span>
        </footer>
      </div>
    </details>
  )
}

function formatUsd(value: number) {
  return new Intl.NumberFormat(activeLanguage(), {
    style: 'currency', currency: 'USD', currencyDisplay: 'code', minimumFractionDigits: 5, maximumFractionDigits: 6,
  }).format(value)
}

function GenerationCost({ row }: { row: AdminAiGeneration }) {
  const { t } = useI18n()
  const { cost, usage } = row.plan
  const number = (value: number) => value.toLocaleString(activeLanguage())
  const range = row.costRange
  const total = cost ? formatUsd(cost.totalUsd) : range
    ? `${formatUsd(range.minUsd)} – ${formatUsd(range.maxUsd)}` : '—'

  return (
    <div className="dash-ai-gen-cost">
      <dl>
        <div>
          <dt>{t('aiGen.cost.input')}</dt>
          <dd>{cost ? formatUsd(cost.inputUsd) : '—'}</dd>
          {usage ? <small>{t('aiGen.tokens', { count: number(usage.inputTokens) })}</small> : null}
        </div>
        <div>
          <dt>{t('aiGen.cost.output')}</dt>
          <dd>{cost ? formatUsd(cost.outputUsd) : '—'}</dd>
          {usage ? <small>{t('aiGen.tokens', { count: number(usage.outputTokens + usage.thoughtTokens) })}</small> : null}
        </div>
        <div className="dash-ai-gen-cost-total">
          <dt>{t('aiGen.cost.total')}</dt>
          <dd>{total}</dd>
          {usage ? <small>{t('aiGen.cost.calls', { count: usage.calls })}</small> : null}
        </div>
      </dl>
      <small className="dash-ai-gen-cost-note">
        {cost ? t('aiGen.cost.note') : range ? t('aiGen.cost.legacy') : t('aiGen.cost.unavailable')}
        {usage?.estimated ? ` ${t('aiGen.cost.estimatedTokens')}` : ''}
        {usage?.cachedInputTokens ? ` ${t('aiGen.cost.cached', { count: number(usage.cachedInputTokens) })}` : ''}
        {cost ? <> <a href="https://ai.google.dev/gemini-api/docs/pricing#gemini-3.1-flash-lite" target="_blank" rel="noreferrer">{t('aiGen.cost.rates')}</a></> : null}
      </small>
    </div>
  )
}
